import type { APIMentionableSelectComponent, Snowflake } from "discord.js";
import { MentionableSelectMenuBuilder } from "discord.js";

export class V2MentionableSelectBuilder extends MentionableSelectMenuBuilder {
	setCustomId(custom_id: Snowflake) {
		if (custom_id.length > 100)
			throw new Error("Max length for custom ids is 100 characters.");

		super.setCustomId(custom_id);
		return this;
	}

	setPlaceholder(placeholder: Snowflake) {
		if (placeholder.length > 150)
			throw new Error("Max length for placeholders is 150 characters.");

		super.setPlaceholder(placeholder);
		return this;
	}

	setMinValues(min_values: number) {
		if (min_values < 0) throw new Error("Min values must be positive.");
		if (min_values > 25) throw new Error("Min values must be less than 25.");

		super.setMinValues(min_values);
		return this;
	}

	setMaxValues(max_values: number) {
		if (max_values < 1) throw new Error("Max values must be positive.");
		if (max_values > 25) throw new Error("Max values must be less than 25.");

		super.setMaxValues(max_values);
		return this;
	}

	setDisabled(disabled: boolean) {
		super.setDisabled(disabled);
		return this;
	}

	setDefaultUsers(default_users: { id: Snowflake }[]) {
		if (default_users.length > 25)
			throw new Error("Cannot have more than 25 default users");

		// Extract just the IDs for addDefaultUsers
		const userIds = default_users.map((user) => user.id);
		this.addDefaultUsers(userIds);
		return this;
	}

	toJSON(): APIMentionableSelectComponent {
		const data = super.toJSON();

		if (!data.custom_id) {
			throw new Error("Mentionable select menu must have a custom_id");
		}

		return data;
	}
}

interface MentionableSelectOptions {
	custom_id: Snowflake;
	placeholder?: Snowflake;
	min_values?: number;
	max_values?: number;
	disabled?: boolean;
	default_users?: Snowflake[];
}

export function makeMentionableSelect(
	options: MentionableSelectOptions,
): V2MentionableSelectBuilder {
	const select = new V2MentionableSelectBuilder();

	select.setCustomId(options.custom_id);
	if (options.placeholder) select.setPlaceholder(options.placeholder);
	if (options.min_values !== undefined) select.setMinValues(options.min_values);
	if (options.max_values !== undefined) select.setMaxValues(options.max_values);
	if (options.disabled) select.setDisabled(options.disabled);
	if (options.default_users)
		select.setDefaultUsers(options.default_users.map((id) => ({ id })));

	return select;
}
